import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { getTranslation, translateCategory } from '../translations';
import xss from 'xss';

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Blog sayfası her zaman Türkçe olacak
  const language = 'tr';

  useEffect(() => {
    const fetchBlog = async () => {
      setLoading(true);
      setError('');
      try {
        const q = query(
          collection(db, 'blogs'),
          where('slug', '==', slug),
          where('status', '==', 'published')
        );
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
          setError(getTranslation(language, 'blogNotFound') || 'Blog not found');
          setLoading(false);
          return;
        }

        const blogData = { id: querySnapshot.docs[0].id, ...querySnapshot.docs[0].data() };
        
        // Normalize markdown content - convert escaped newlines to actual newlines
        if (blogData.content_md && typeof blogData.content_md === 'string') {
          blogData.content_md = blogData.content_md
            .replace(/\\n/g, '\n')
            .replace(/\\r/g, '\r')
            .replace(/\\t/g, '\t');
        }
        
        setBlog(blogData);

        // Update document title for SEO
        if (blogData.seo?.title) {
          document.title = blogData.seo.title;
        } else if (blogData.title) {
          document.title = blogData.title;
        }

        // Update meta description
        if (blogData.seo?.description) {
          let metaDescription = document.querySelector('meta[name="description"]');
          if (!metaDescription) {
            metaDescription = document.createElement('meta');
            metaDescription.setAttribute('name', 'description');
            document.head.appendChild(metaDescription);
          }
          metaDescription.setAttribute('content', blogData.seo.description);
        }
      } catch (err) {
        console.error('Error fetching blog:', err);
        setError(getTranslation(language, 'errorLoadingBlog') || 'Error loading blog');
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [slug, language]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  // Detect if content is HTML (contains HTML tags)
  const isHtmlContent = (content) => {
    if (!content || typeof content !== 'string') return false;
    // Check for HTML tags (basic detection)
    const htmlTagPattern = /<[a-z][\s\S]*>/i;
    return htmlTagPattern.test(content);
  };

  // Sanitize HTML content
  const sanitizeHtml = (html) => {
    if (!html || typeof html !== 'string') return '';
    
    const options = {
      whiteList: {
        div: ['style', 'class'],
        img: ['src', 'alt', 'title', 'width', 'height', 'style'],
        // Yazı etiketlerine de style izni veriyoruz:
        h1: ['style'], 
        h2: ['style'], 
        h3: ['style'],
        p: ['style'], 
        span: ['style'],
        // Diğerleri...
        a: ['href', 'title', 'target', 'rel'],
        hr: [], br: [], b: [], strong: [], i: [], em: [],
        ul: [], ol: [], li: []
      },
      stripIgnoreTag: true,
      stripIgnoreTagBody: ['script', 'style']
    };
    return xss(html, options);
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
      }}>
        <div style={{ fontSize: 18, color: '#666' }}>
          {getTranslation(language, 'loading') || 'Loading...'}
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        padding: 40
      }}>
        <div style={{ fontSize: 18, color: '#d32f2f', marginBottom: 24 }}>
          {error || getTranslation(language, 'blogNotFound') || 'Blog not found'}
        </div>
        <button
          onClick={() => navigate('/blogs')}
          style={{
            padding: '12px 24px',
            background: '#1a1a1a',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#333';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#1a1a1a';
          }}
        >
          {getTranslation(language, 'backToBlogs') || 'Back to Blogs'}
        </button>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .blog-html-content h1 {
          font-size: 36px;
          font-weight: 700;
          margin-top: 32px;
          margin-bottom: 16px;
          color: #1a1a1a;
        }
        .blog-html-content h2 {
          font-size: 30px;
          font-weight: 700;
          margin-top: 28px;
          margin-bottom: 14px;
          color: #1a1a1a;
        }
        .blog-html-content h3 {
          font-size: 24px;
          font-weight: 700;
          margin-top: 24px;
          margin-bottom: 12px;
          color: #1a1a1a;
        }
        .blog-html-content p {
          margin-bottom: 16px;
        }
        .blog-html-content ul,
        .blog-html-content ol {
          margin-bottom: 16px;
          padding-left: 24px;
        }
        .blog-html-content li {
          margin-bottom: 8px;
        }
        .blog-html-content strong {
          font-weight: 700;
        }
        .blog-html-content em {
          font-style: italic;
        }
        .blog-html-content a {
          color: #1a1a1a;
          text-decoration: underline;
        }
        .blog-html-content code {
          background: #f5f5f5;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 16px;
          font-family: monospace;
        }
        .blog-html-content pre {
          background: #f5f5f5;
          padding: 16px;
          border-radius: 8px;
          overflow: auto;
          margin-bottom: 16px;
        }
        .blog-html-content blockquote {
          border-left: 4px solid #ddd;
          padding-left: 16px;
          margin-left: 0;
          margin-bottom: 16px;
          font-style: italic;
          color: #666;
        }
        .blog-html-content img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin-bottom: 16px;
        }
      `}</style>
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        padding: '80px 20px 40px'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          width: '100%',
          padding: '0 20px',
          boxSizing: 'border-box'
        }}>
        {/* Back Button */}
        <button
          onClick={() => navigate('/blogs')}
          style={{
            marginBottom: 32,
            padding: '10px 20px',
            background: '#fff',
            color: '#1a1a1a',
            border: '2px solid #ddd',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#1a1a1a';
            e.currentTarget.style.background = '#f5f5f5';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#ddd';
            e.currentTarget.style.background = '#fff';
          }}
        >
          ← {getTranslation(language, 'backToBlogs') || 'Back to Blogs'}
        </button>

        {/* Article Card */}
        <article style={{
          background: '#fff',
          borderRadius: 16,
          overflow: 'hidden',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          marginBottom: 32
        }}>
          {/* Cover Image */}
          {blog.cover_image && (
            <div style={{
              width: '100%',
              height: 400,
              backgroundImage: `url(${blog.cover_image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }} />
          )}

          {/* Content */}
          <div style={{ padding: 48 }}>
            {/* Category */}
            {blog.category && (
              <div style={{
                fontSize: 14,
                fontWeight: 600,
                color: '#666',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: 16
              }}>
                {translateCategory(blog.category, language)}
              </div>
            )}

            {/* Title */}
            <h1 style={{
              fontSize: 42,
              fontWeight: 700,
              color: '#1a1a1a',
              marginBottom: 24,
              lineHeight: 1.2,
              letterSpacing: '-0.02em'
            }}>
              {blog.title}
            </h1>

            {/* Description */}
            {blog.description && (
              <p style={{
                fontSize: 20,
                color: '#666',
                lineHeight: 1.6,
                marginBottom: 32
              }}>
                {blog.description}
              </p>
            )}

            {/* Meta Info */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 24,
              marginBottom: 40,
              paddingBottom: 32,
              borderBottom: '2px solid #eee',
              fontSize: 14,
              color: '#999'
            }}>
              {blog.author?.name && (
                <div>
                  <strong style={{ color: '#666' }}>
                    {getTranslation(language, 'author') || 'Author'}: 
                  </strong> {blog.author.name}
                </div>
              )}
              {blog.published_at && (
                <div>
                  <strong style={{ color: '#666' }}>
                    {getTranslation(language, 'published') || 'Published'}: 
                  </strong> {formatDate(blog.published_at)}
                </div>
              )}
              {blog.reading_time && (
                <div>
                  <strong style={{ color: '#666' }}>
                    {getTranslation(language, 'readingTime') || 'Reading time'}: 
                  </strong> {blog.reading_time} {getTranslation(language, 'minutes') || 'minutes'}
                </div>
              )}
            </div>

            {/* Content (HTML or Markdown) */}
            {blog.content_md && (
              <div style={{
                fontSize: 18,
                lineHeight: 1.8,
                color: '#333'
              }}>
                {isHtmlContent(blog.content_md) ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(blog.content_md)
                    }}
                    style={{
                      // Styles applied via CSS classes below
                    }}
                    className="blog-html-content"
                  />
                ) : (
                  <ReactMarkdown
                    components={{
                      h1: ({node, children, ...props}) => <h1 style={{ fontSize: 36, fontWeight: 700, marginTop: 32, marginBottom: 16, color: '#1a1a1a' }} {...props}>{children}</h1>,
                      h2: ({node, children, ...props}) => <h2 style={{ fontSize: 30, fontWeight: 700, marginTop: 28, marginBottom: 14, color: '#1a1a1a' }} {...props}>{children}</h2>,
                      h3: ({node, children, ...props}) => <h3 style={{ fontSize: 24, fontWeight: 700, marginTop: 24, marginBottom: 12, color: '#1a1a1a' }} {...props}>{children}</h3>,
                      p: ({node, ...props}) => <p style={{ marginBottom: 16 }} {...props} />,
                      ul: ({node, ...props}) => <ul style={{ marginBottom: 16, paddingLeft: 24 }} {...props} />,
                      ol: ({node, ...props}) => <ol style={{ marginBottom: 16, paddingLeft: 24 }} {...props} />,
                      li: ({node, ...props}) => <li style={{ marginBottom: 8 }} {...props} />,
                      strong: ({node, ...props}) => <strong style={{ fontWeight: 700 }} {...props} />,
                      em: ({node, ...props}) => <em style={{ fontStyle: 'italic' }} {...props} />,
                      a: ({node, children, ...props}) => <a style={{ color: '#1a1a1a', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>,
                      code: ({node, ...props}) => <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4, fontSize: 16, fontFamily: 'monospace' }} {...props} />,
                      pre: ({node, ...props}) => <pre style={{ background: '#f5f5f5', padding: 16, borderRadius: 8, overflow: 'auto', marginBottom: 16 }} {...props} />,
                      blockquote: ({node, ...props}) => <blockquote style={{ borderLeft: '4px solid #ddd', paddingLeft: 16, marginLeft: 0, marginBottom: 16, fontStyle: 'italic', color: '#666' }} {...props} />,
                      img: ({node, alt, ...props}) => <img alt={alt || ''} style={{ maxWidth: '100%', height: 'auto', borderRadius: 8, marginBottom: 16 }} {...props} />
                    }}
                  >
                    {blog.content_md}
                  </ReactMarkdown>
                )}
              </div>
            )}
          </div>
        </article>
      </div>
    </div>
    </>
  );
};

export default BlogDetail;
